import React, { useState } from "react";
import {
  Box,
  Card,
  Button,
  Text,
  Page,
  Input,
  FormField,
  InputArea,
  Dropdown,
  SectionHelper,
  Layout,
  Cell,
  Breadcrumbs,
} from "@wix/design-system";
import "@wix/design-system/styles.global.css";
import { TrademarkRequest } from "./TrademarkRequestsList";
import { createClient } from "@wix/sdk";
import { dashboard } from "@wix/dashboard";
import { siteProperties } from "@wix/business-tools";
import { UsaStates } from "usa-states";

interface CreateTrademarkRequestProps {
  onSave: (request: Partial<TrademarkRequest>) => Promise<void>;
  onCancel: () => void;
  initialData?: Partial<TrademarkRequest>;
  isEdit?: boolean;
  instanceData?: any;
  client?: any;
}

// Get US states from npm package
const usStatesInstance = new UsaStates();
const US_STATES = usStatesInstance.states.map((state: { abbreviation: string; name: string }) => ({
  id: state.abbreviation,
  value: state.name,
}));

function inIframe() {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
}

const CreateTrademarkRequest: React.FC<CreateTrademarkRequestProps> = ({
  onSave,
  onCancel,
  initialData,
  isEdit = false,
  instanceData,
  client: propClient,
}) => {
  const [wixClient, setWixClient] = React.useState<any>(null);
  const [siteProps, setSiteProps] = React.useState<any>(null);

  // Initialize Wix client for site properties
  React.useEffect(() => {
    if (propClient) {
      setWixClient(propClient);
    } else if (inIframe()) {
      try {
        const auth = dashboard.auth();
        const c = createClient({
          host: dashboard.host(),
          auth: auth,
          modules: {
            dashboard,
            siteProperties,
          },
        });
        setWixClient(c);
      } catch (error) {
        console.error("Error creating Wix client:", error);
      }
    }
  }, [propClient]);

  // Fetch site properties when client is ready and it's a new request
  React.useEffect(() => {
    const fetchSiteProperties = async () => {
      if (!wixClient || isEdit || initialData) return;

      try {
        const response = await wixClient.siteProperties.getSiteProperties();
        console.log("=== SITE PROPERTIES ===");
        console.log("Site properties:", JSON.stringify(response, null, 2));
        setSiteProps(response);
      } catch (error: any) {
        console.error("Error fetching site properties:", error);
        // Don't show error to user, just log it
      }
    };

    fetchSiteProperties();
  }, [wixClient, isEdit, initialData]);

  // Auto-fill applicant name and email from instance data or site properties
  const getApplicantName = React.useCallback(() => {
    if (initialData?.applicantName) return initialData.applicantName;
    // Prefer businessName from site properties, then siteDisplayName from site properties, then instance data
    if (siteProps?.properties?.businessName) {
      return siteProps.properties.businessName;
    }
    if (siteProps?.properties?.siteDisplayName) {
      return siteProps.properties.siteDisplayName;
    }
    return instanceData?.site?.siteDisplayName || "";
  }, [initialData, instanceData, siteProps]);

  const getApplicantEmail = React.useCallback(() => {
    if (initialData?.applicantEmail) return initialData.applicantEmail;
    // Prefer email from site properties
    if (siteProps?.properties?.email) {
      return siteProps.properties.email;
    }
    return instanceData?.site?.ownerInfo?.email || "";
  }, [initialData, instanceData, siteProps]);

  // Initialize form data with auto-filled values from instance data and site properties
  const initialFormData = React.useMemo(() => {
    const name = getApplicantName();
    const email = getApplicantEmail();
    const address = siteProps?.properties?.address;
    
    // Build address string from streetNumber and street
    let applicantAddress = "";
    if (address?.street) {
      const streetParts = [address.streetNumber, address.street].filter(Boolean);
      if (streetParts.length > 0) {
        applicantAddress = streetParts.join(" ").trim();
      }
    }
    
    return {
      trademarkName: "",
      description: "",
      goodsAndServices: "",
      applicantName: name,
      applicantEmail: email,
      applicantAddress: applicantAddress,
      applicantCity: address?.city || "",
      applicantState: address?.state || "",
      applicantZipCode: address?.zip || "",
      applicantCountry: address?.country === "US" ? "US" : (address?.country || "US"),
      status: "draft" as const,
      ...initialData,
    };
  }, [initialData, siteProps, getApplicantName, getApplicantEmail]);

  const [formData, setFormData] = useState<Partial<TrademarkRequest>>(initialFormData);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Update form data when instanceData or siteProps changes (for auto-fill)
  React.useEffect(() => {
    if (!isEdit && !initialData && siteProps) {
      const name = getApplicantName();
      const email = getApplicantEmail();
      const address = siteProps?.properties?.address;
      
      setFormData((prev) => {
        const updates: Partial<TrademarkRequest> = {};
        
        // Auto-fill applicant name if available and not already set
        if (name && !prev.applicantName) {
          updates.applicantName = name;
        }
        
        // Auto-fill email if available and not already set
        if (email && !prev.applicantEmail) {
          updates.applicantEmail = email;
        }
        
        // Auto-fill address fields if available
        if (address) {
          // Build address string from streetNumber and street
          if (address.street && !prev.applicantAddress) {
            const streetParts = [address.streetNumber, address.street].filter(Boolean);
            if (streetParts.length > 0) {
              updates.applicantAddress = streetParts.join(" ").trim();
            }
          }
          
          // Auto-fill city if available and not already set
          if (address.city && !prev.applicantCity) {
            updates.applicantCity = address.city;
          }
          
          // Auto-fill state if available and not already set
          if (address.state && !prev.applicantState) {
            updates.applicantState = address.state;
          }
          
          // Auto-fill zip code if available and not already set
          if (address.zip && !prev.applicantZipCode) {
            updates.applicantZipCode = address.zip;
          }
          
          // Auto-fill country if available (default to US if country is invalid)
          if (address.country) {
            // Only update if country is valid (US states dropdown expects US)
            if (address.country === "US" && prev.applicantCountry !== "US") {
              updates.applicantCountry = "US";
            }
          } else if (!prev.applicantCountry || prev.applicantCountry !== "US") {
            // Default to US if no country in address
            updates.applicantCountry = "US";
          }
        }
        
        // Only update if there are changes
        if (Object.keys(updates).length > 0) {
          return { ...prev, ...updates };
        }
        
        return prev;
      });
    }
  }, [instanceData, siteProps, isEdit, initialData, getApplicantName, getApplicantEmail]);

  const handleSubmit = async (status: "draft" | "submitted") => {
    setError(null);

    // Validation
    if (!formData.trademarkName?.trim()) {
      setError("Trademark name is required");
      return;
    }
    if (!formData.applicantName?.trim()) {
      setError("Applicant name is required");
      return;
    }
    if (!formData.applicantEmail?.trim()) {
      setError("Applicant email is required");
      return;
    }
    if (!formData.applicantAddress?.trim()) {
      setError("Applicant address is required");
      return;
    }
    if (!formData.applicantCity?.trim()) {
      setError("Applicant city is required");
      return;
    }
    if (!formData.applicantState) {
      setError("Applicant state is required");
      return;
    }
    if (!formData.applicantZipCode?.trim()) {
      setError("Applicant zip code is required");
      return;
    }

    try {
      setIsLoading(true);
      await onSave({ ...formData, status });
    } catch (err: any) {
      setError(err.message || "Failed to save trademark request");
      setIsLoading(false);
    }
  };

  const updateField = (field: keyof TrademarkRequest, value: any) => {
    setFormData({ ...formData, [field]: value });
  };

  return (
    <Page height="100vh">
      <Page.Header
        title={isEdit ? "Edit Trademark Request" : "Create Trademark Request"}
        subtitle={
          isEdit
            ? "Update your trademark request information."
            : "Fill out the form below to create a new US trademark request."
        }
        actionsBar={
          <Box gap="SP2">
            <Button skin="inverted" onClick={onCancel} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              onClick={() => handleSubmit("draft")}
              disabled={isLoading}
              skin="inverted"
            >
              Save Draft
            </Button>
            <Button onClick={() => handleSubmit("submitted")} disabled={isLoading}>
              Submit Request
            </Button>
          </Box>
        }
        breadcrumbs={
          <Breadcrumbs
            activeId="2"
            items={[
              { id: "1", value: "Trademark Requests" },
              { id: "2", value: isEdit ? "Edit Request" : "Create Request" },
            ]}
            onClick={() => {}}
          />
        }
        showBackButton
        onBackClicked={onCancel}
      />
      <Page.Content>
        {error && (
          <Box marginBottom="SP3">
            <SectionHelper title="Error" skin="danger" onClose={() => setError(null)}>
              <Text size="small">{error}</Text>
            </SectionHelper>
          </Box>
        )}
        <Layout>
          <Cell span={8}>
            <Layout>
              <Cell>
                <Card>
                  <Card.Header
                    title="Trademark Information"
                    subtitle="Enter the details about the trademark you want to register."
                  />
                  <Card.Divider />
                  <Card.Content>
                    <Box direction="vertical" gap="SP4">
                      <FormField label="Trademark Name" required>
                        <Input
                          value={formData.trademarkName || ""}
                          onChange={(e) => updateField("trademarkName", e.target.value)}
                          placeholder="Enter trademark name"
                        />
                      </FormField>
                      <FormField label="Description" required>
                        <InputArea
                          value={formData.description || ""}
                          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => updateField("description", e.target.value)}
                          placeholder="Describe your trademark"
                          rows={4}
                        />
                      </FormField>
                      <FormField label="Goods and Services" required>
                        <InputArea
                          value={formData.goodsAndServices || ""}
                          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                            updateField("goodsAndServices", e.target.value)
                          }
                          placeholder="Describe the goods and services associated with this trademark"
                          rows={4}
                        />
                      </FormField>
                    </Box>
                  </Card.Content>
                </Card>
              </Cell>
              <Cell>
                <Card>
                  <Card.Header
                    title="Applicant Information"
                    subtitle="Enter the applicant's contact and address information."
                  />
                  <Card.Divider />
                  <Card.Content>
                    <Box direction="vertical" gap="SP4">
                      <FormField label="Applicant Name" required>
                        <Input
                          value={formData.applicantName || ""}
                          onChange={(e) => updateField("applicantName", e.target.value)}
                          placeholder="Full legal name"
                        />
                      </FormField>
                      <FormField label="Email" required>
                        <Input
                          type="email"
                          value={formData.applicantEmail || ""}
                          onChange={(e) => updateField("applicantEmail", e.target.value)}
                          placeholder="email@example.com"
                        />
                      </FormField>
                      <FormField label="Address" required>
                        <Input
                          value={formData.applicantAddress || ""}
                          onChange={(e) => updateField("applicantAddress", e.target.value)}
                          placeholder="Street address"
                        />
                      </FormField>
                      <Layout>
                        <Cell span={6}>
                          <FormField label="City" required>
                            <Input
                              value={formData.applicantCity || ""}
                              onChange={(e) => updateField("applicantCity", e.target.value)}
                              placeholder="City"
                            />
                          </FormField>
                        </Cell>
                        <Cell span={3}>
                          <FormField label="State" required>
                            <Dropdown
                              options={US_STATES}
                              selectedId={formData.applicantState || ""}
                              onSelect={(option: any) =>
                                updateField("applicantState", option.id)
                              }
                              placeholder="Select state"
                            />
                          </FormField>
                        </Cell>
                        <Cell span={3}>
                          <FormField label="Zip Code" required>
                            <Input
                              value={formData.applicantZipCode || ""}
                              onChange={(e) =>
                                updateField("applicantZipCode", e.target.value)
                              }
                              placeholder="12345"
                            />
                          </FormField>
                        </Cell>
                      </Layout>
                    </Box>
                  </Card.Content>
                </Card>
              </Cell>
            </Layout>
          </Cell>
          <Cell span={4}>
            <Page.Sticky>
              <Card>
                <Card.Header
                  title="Request Status"
                  subtitle="Current status of your trademark request."
                />
                <Card.Divider />
                <Card.Content>
                  <Box direction="vertical" gap="SP2">
                    <Text size="small" weight="bold">
                      Status:{" "}
                      <Text size="small" weight="normal">
                        {formData.status === "draft" ? "Draft" : "Submitted"}
                      </Text>
                    </Text>
                    {formData.createdAt && (
                      <Text size="small" secondary>
                        Created: {new Date(formData.createdAt).toLocaleDateString()}
                      </Text>
                    )}
                    {formData.updatedAt && (
                      <Text size="small" secondary>
                        Updated: {new Date(formData.updatedAt).toLocaleDateString()}
                      </Text>
                    )}
                  </Box>
                </Card.Content>
              </Card>
            </Page.Sticky>
          </Cell>
          <Cell>
            <Page.Footer divider>
              <Page.Footer.End>
                <Box gap="SP2">
                  <Button priority="secondary" onClick={onCancel} disabled={isLoading}>
                    Cancel
                  </Button>
                  <Button
                    skin="inverted"
                    onClick={() => handleSubmit("draft")}
                    disabled={isLoading}
                  >
                    {isLoading ? "Saving..." : "Save Draft"}
                  </Button>
                  <Button onClick={() => handleSubmit("submitted")} disabled={isLoading}>
                    {isLoading ? "Submitting..." : "Submit Request"}
                  </Button>
                </Box>
              </Page.Footer.End>
            </Page.Footer>
          </Cell>
        </Layout>
      </Page.Content>
    </Page>
  );
};

export default CreateTrademarkRequest;

